import styles from "../../communication-chat.module.css";
import { useEffect, useRef, useState } from "react";
import ContentLoader from "react-content-loader";
import moment from "moment";
import { Edit2, Check, X, Smile, Plus } from "react-feather";
import EmojiPicker from "emoji-picker-react";
import IdentityMessageFileViewer from "./identityMessageFileViewer";
import { useAuth0 } from "@auth0/auth0-react";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const IdentityChatMessages = ({
  messages,
  messageLoader,
  currentUserId,
  resolveAuthorName,
  onEditMessage,
  onToggleReaction,
  onLoadOlderMessages,
  hasMoreOlderMessages,
  isLoadingOlderMessages,
}) => {
  const chatAreaRef = useRef(null);
  const lastMessageSidRef = useRef(null);
  const [editingSid, setEditingSid] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [openReactionPickerSid, setOpenReactionPickerSid] = useState(null);
  const [showFullEmojiFor, setShowFullEmojiFor] = useState(null);
  const fullPickerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isFetchingOlderRef = useRef(false);

  const { isAuthenticated, user } = useAuth0();

  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;

    const newLastSid = messages.length
      ? messages[messages.length - 1].sid
      : null;
    const isAppend = newLastSid !== lastMessageSidRef.current;
    lastMessageSidRef.current = newLastSid;

    // A prepend (older messages loaded) shouldn't touch scroll at all here —
    // the separate scroll-restore effect handles keeping the view stable.
    if (!isAppend) return;

    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    scrollToBottom();

    const mediaEls = el.querySelectorAll("img, video");
    mediaEls.forEach((mediaEl) => {
      mediaEl.addEventListener("load", scrollToBottom);
      mediaEl.addEventListener("loadeddata", scrollToBottom);
    });
    return () => {
      mediaEls.forEach((mediaEl) => {
        mediaEl.removeEventListener("load", scrollToBottom);
        mediaEl.removeEventListener("loadeddata", scrollToBottom);
      });
    };
  }, [messages]);

  // Close the full picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showFullEmojiFor &&
        fullPickerRef.current &&
        !fullPickerRef.current.contains(e.target)
      ) {
        setShowFullEmojiFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFullEmojiFor]);

  const handleScroll = () => {
    const el = chatAreaRef.current;
    if (!el || isFetchingOlderRef.current || !hasMoreOlderMessages) return;
    if (el.scrollTop < 60) {
      isFetchingOlderRef.current = true; // blocks any further calls immediately
      prevScrollHeightRef.current = el.scrollHeight;
      onLoadOlderMessages?.();
    }
  };

  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;

    const newLastSid = messages.length
      ? messages[messages.length - 1].sid
      : null;
    const isAppend = newLastSid !== lastMessageSidRef.current;
    lastMessageSidRef.current = newLastSid;

    if (!isAppend) return;

    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    // Media load events fire asynchronously — possibly well after this
    // effect ran, by which point the user may have scrolled up to read
    // history or load older messages. Only auto-follow if they're still
    // near the bottom; never yank them back down mid-scroll.
    const scrollToBottomIfNearBottom = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < 150) {
        scrollToBottom();
      }
    };

    scrollToBottom(); // the append itself (initial load / new message) always scrolls

    const mediaEls = el.querySelectorAll("img, video");
    mediaEls.forEach((mediaEl) => {
      mediaEl.addEventListener("load", scrollToBottomIfNearBottom);
      mediaEl.addEventListener("loadeddata", scrollToBottomIfNearBottom);
    });
    return () => {
      mediaEls.forEach((mediaEl) => {
        mediaEl.removeEventListener("load", scrollToBottomIfNearBottom);
        mediaEl.removeEventListener("loadeddata", scrollToBottomIfNearBottom);
      });
    };
  }, [messages]);

  useEffect(() => {
    if (!isLoadingOlderMessages) {
      isFetchingOlderRef.current = false;
    }
  }, [isLoadingOlderMessages]);

  const startEdit = (msg) => {
    setEditingSid(msg.sid);
    setEditValue(msg.message || "");
  };
  const cancelEdit = () => {
    setEditingSid(null);
    setEditValue("");
  };
  const confirmEdit = async (msg) => {
    if (!editValue.trim()) return;
    await onEditMessage(msg, editValue);
    setEditingSid(null);
    setEditValue("");
  };

  const handleReactionClick = (msg, emoji) => {
    setOpenReactionPickerSid(null);
    setShowFullEmojiFor(null);
    onToggleReaction(msg, emoji);
  };

  const handleMediaSettled = () => {
    const el = chatAreaRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 300) {
      el.scrollTop = el.scrollHeight;
    }
  };

  return (
    <>
      {isLoadingOlderMessages && ( // NEW
        <div
          style={{
            textAlign: "center",
            padding: "8px 0",
            fontSize: "12px",
            color: "#343434",
          }}
        >
          Loading earlier messages…
        </div>
      )}
      {messageLoader && (
        <div className={`${styles.ChatBoxContentLoader}`}>
          <ContentLoader width={400} height={300}>
            <rect x="27" y="40" rx="3" ry="3" width="260" height="10"></rect>
            <rect x="27" y="60" rx="3" ry="3" width="240" height="10"></rect>
            <rect x="27" y="80" rx="3" ry="3" width="180" height="10"></rect>
          </ContentLoader>
        </div>
      )}

      <div
        ref={chatAreaRef}
        className={`${styles.ChatViewSec}`}
        style={{ overflowY: "scroll", maxHeight: "300px" }}
        onScroll={handleScroll}
      >
        {messages.length > 0 &&
          messages.map((msg, i) => {
            const isMine = String(msg.author) === String(currentUserId);
            const wasEdited =
              msg.updatedAt &&
              msg.createdAt &&
              new Date(msg.updatedAt).getTime() !==
              new Date(msg.createdAt).getTime();
            const isEditing = editingSid === msg.sid;
            const isCallLog = msg.message?.startsWith("📞");
            const canEdit = isMine && !!msg.message && !isEditing && !isCallLog;
            const reactionEntries = Object.entries(msg.reactions || {}).filter(
              ([, ids]) => ids.length > 0
            );



            const isSMSChatTeamUpdate = isAuthenticated && user["https://propertydek.com/permissions"].indexOf("smschat-team:update") > -1;

            return (
              <div
                className={
                  isMine
                    ? `${styles.MessageBlog} ${styles.Receiver}`
                    : `${styles.MessageBlog}`
                }
                key={msg.sid || i}
              >
                <div className={`${styles.MessageBlogInner}`}>
                  <div className={`${styles.MessageBlogCon}`}>
                    <p className={`${styles.MessageName}`}>
                      {isMine ? "You" : resolveAuthorName(msg.author)}{" "}
                      <span className={`${styles.MessageTime}`}>
                        {moment(msg.createdAt).format("MMMM Do, h:mm A")}
                        {wasEdited && " (edited)"}
                      </span>
                      <span
                        className={`${styles.CopyText}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {canEdit && isSMSChatTeamUpdate && (
                          <span
                            onClick={() => startEdit(msg)}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                            }}
                          >
                            <Edit2 size={14} /> Edit
                          </span>
                        )}
                        <span
                          onClick={() =>
                            setOpenReactionPickerSid(
                              openReactionPickerSid === msg.sid ? null : msg.sid
                            )
                          }
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                          }}
                        >
                          <Smile size={14} /> React
                        </span>
                      </span>
                    </p>

                    {openReactionPickerSid === msg.sid && (
                      <div style={{ position: "relative" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "#fff",
                            border: "1px solid #e1e1e1",
                            borderRadius: "20px",
                            padding: "4px 8px",
                            marginBottom: "6px",
                            width: "fit-content",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          {QUICK_REACTIONS.map((emoji) => (
                            <span
                              key={emoji}
                              onClick={() => handleReactionClick(msg, emoji)}
                              style={{ cursor: "pointer", fontSize: "18px" }}
                            >
                              {emoji}
                            </span>
                          ))}
                          <span
                            onClick={() =>
                              setShowFullEmojiFor(
                                showFullEmojiFor === msg.sid ? null : msg.sid
                              )
                            }
                            style={{
                              cursor: "pointer",
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              background: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Plus size={14} />
                          </span>
                        </div>

                        {showFullEmojiFor === msg.sid && (
                          <div
                            ref={fullPickerRef}
                            style={{
                              position: "absolute",
                              top: "34px",
                              left: 0,
                              zIndex: 20,
                              boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                              borderRadius: "8px",
                            }}
                          >
                            <EmojiPicker
                              onEmojiClick={(emojiData) =>
                                handleReactionClick(msg, emojiData.emoji)
                              }
                              height={350}
                              width={280}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {isEditing ? (
                      <div style={{ width: "280px" }}>
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          style={{
                            width: "100%",
                            minHeight: "50px",
                            borderRadius: "6px",
                            border: "1px solid #e1e1e1",
                            padding: "8px",
                            fontSize: "13px",
                            resize: "none",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                            marginTop: "4px",
                          }}
                        >
                          <button
                            onClick={cancelEdit}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#666",
                            }}
                          >
                            <X size={16} />
                          </button>
                          <button
                            onClick={() => confirmEdit(msg)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#502688",
                            }}
                          >
                            <Check size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {msg.message && (
                          <div className={`${styles.MessageDesMain}`}>
                            <p className={`${styles.MessageDes}`}>
                              {msg.message}
                            </p>
                          </div>
                        )}

                        {msg.media && msg.media.length > 0 && (
                          <div className={`${styles.MessageFileSec}`}>
                            <IdentityMessageFileViewer
                              media={msg.media}
                              onMediaLoad={handleMediaSettled}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {reactionEntries.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          marginTop: "4px",
                          flexWrap: "wrap",
                        }}
                      >
                        {reactionEntries.map(([emoji, ids]) => {
                          const mine = ids.includes(String(currentUserId));
                          return (
                            <span
                              key={emoji}
                              onClick={() => onToggleReaction(msg, emoji)}
                              style={{
                                cursor: "pointer",
                                fontSize: "12px",
                                background: mine ? "#f0e6ff" : "#f5f5f5",
                                border: mine
                                  ? "1px solid #502688"
                                  : "1px solid #e1e1e1",
                                borderRadius: "12px",
                                padding: "2px 8px",
                              }}
                            >
                              {emoji} {ids.length}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

        {!messageLoader && messages.length === 0 && (
          <div className={`${styles.ChatBoxnoDataSec}`}>
            <img src="/static/img/chatIcon.png" alt="logo" />
            <p>Start a New Conversation</p>
          </div>
        )}
      </div>
    </>
  );
};

export default IdentityChatMessages;
